<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';

interface Props {
  fallback?: 'default' | 'minimal' | 'custom';
}

const props = withDefaults(defineProps<Props>(), {
  fallback: 'default',
});

const emit = defineEmits<{
  (e: 'error', error: Error, info: string): void;
}>();

const hasError = ref(false);
const errorMessage = ref('');
const errorStack = ref('');

onErrorCaptured((error: Error, _instance, info: string) => {
  hasError.value = true;
  errorMessage.value = error.message;
  errorStack.value = error.stack || '';

  // Log to console (could later integrate with Overwatch SDK)
  console.error('Vue Error Boundary caught error:', error, info);

  // Emit for parent handling
  emit('error', error, info);

  // Prevent error from propagating
  return false;
});

function retry() {
  hasError.value = false;
  errorMessage.value = '';
  errorStack.value = '';
}

function goHome() {
  window.location.href = '/';
}

// Expose for custom slot usage
defineExpose({ retry, goHome, errorMessage, errorStack });
</script>

<template>
  <slot v-if="!hasError" />

  <!-- Default fallback -->
  <div v-else-if="props.fallback === 'default'" class="error-boundary p-4">
    <Card class="mx-auto max-w-lg">
      <template #title>
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle mr-2 text-2xl text-red-500" />
          <span>Something went wrong</span>
        </div>
      </template>
      <template #content>
        <p class="mb-4 text-gray-600">{{ errorMessage }}</p>
        <div class="flex gap-2">
          <Button label="Try Again" icon="pi pi-refresh" @click="retry" />
          <Button
            label="Go Home"
            icon="pi pi-home"
            severity="secondary"
            @click="goHome"
          />
        </div>
      </template>
    </Card>
  </div>

  <!-- Minimal fallback -->
  <div v-else-if="props.fallback === 'minimal'" class="p-4 text-center">
    <p class="mb-2 text-red-500">
      <i class="pi pi-exclamation-circle mr-1" />
      Error loading content
    </p>
    <Button label="Retry" size="small" @click="retry" />
  </div>

  <!-- Custom slot fallback -->
  <slot
    v-else
    name="error"
    :error="errorMessage"
    :stack="errorStack"
    :retry="retry"
    :go-home="goHome"
  />
</template>

<style scoped>
.error-boundary {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
